import { apiRequest } from '@/lib/api';
import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { ConversationBubble } from './ConversationBubble';
import { EmmaTypingIndicator } from './EmmaTypingIndicator';
import { ToolActivityIndicator } from './ToolActivityIndicator';
import { EmmaCapabilitiesExplainer } from './EmmaCapabilitiesExplainer';
import { ToolErrorMessage } from './ToolErrorMessage';
import { ErrorRecoveryDialog } from '../shared/ErrorRecoveryDialog';
import { EmmaEntityContext, useEmmaEntityExtraction, extractEmmaEntities, type EntityReference } from './EmmaEntityContext';
import { EmmaContextRenderer } from './EmmaContextRenderer';
import { RecommendationCarousel } from './RecommendationCarousel';
import { ClassificationSuggestionCard } from './ClassificationSuggestionCard';
import { useEmmaWebSocket } from '../../hooks/emma/useEmmaWebSocket';
import { useEmmaContext } from '../../contexts/EmmaContext';
import { useToolActivity } from '../../hooks/emma/useToolActivity';
import { useEmmaSessionSync } from '../../hooks/emma/useEmmaSessionSync';
import { WorkflowStatusIndicator } from './WorkflowStatusIndicator';
import { EmergencyHelpButton } from './EmergencyHelpButton';
import type { EmmaChatProps, Message, AgentRecommendation, WorkflowStatus, ClassificationSuggestion } from '../../types/emma.types';
// Story 4: Advanced Emma capabilities
import { useConversationMemory } from '../../hooks/useConversationMemory';
import { emmaResponseCache, generateCacheKey } from '../../utils/responseCache';
import { performanceMonitor, usePerformanceTracking } from '../../utils/performanceMonitor';

/**
 * EmmaChat Component - Main conversational interface
 * Integrates with agent-first UI design system and Epic 8 React TypeScript architecture
 * Provides real-time WebSocket communication with <100ms response acknowledgment
 * EMMA-HARDEN-002: Enhanced with CitationBadge, EmmaTypingIndicator, and ErrorRecoveryDialog
 */
export function EmmaChat({
  className = '',
  onMessageSent,
  onRecommendationSelected,
  initialContext,
  theme = 'auto',
  campaignId,
  context: propContext
}: EmmaChatProps) {
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [typingStatus, setTypingStatus] = useState<'thinking' | 'searching' | 'processing'>('thinking');
  const [sourcesChecked, setSourcesChecked] = useState(0);
  const [workflowStatus, setWorkflowStatus] = useState<WorkflowStatus | null>(null);
  const [currentError, setCurrentError] = useState<any>(null);
  const [toolError, setToolError] = useState<any>(null);
  const [isEmergencyHelpVisible, setIsEmergencyHelpVisible] = useState(true);
  const [lastActivityTime, setLastActivityTime] = useState<Date>(new Date());
  const [showCapabilities, setShowCapabilities] = useState(false);
  const [selectedEntityId, setSelectedEntityId] = useState<string | null>(null);
  
  // Story 2: Emma Context Renderer search results
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [selectedSearchResult, setSelectedSearchResult] = useState<any | null>(null);
  const [recommendationsForEntity, setRecommendationsForEntity] = useState<{
    entityId: string;
    entityType: 'client' | 'partner' | 'solution';
    entityName: string;
  } | null>(null);
  
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const isDuplicateEmmaMessage = useCallback((prev: Message[], candidate: Message): boolean => {
    if (!candidate || !candidate.id) return false;
    if (prev.some(m => m.id === candidate.id)) return true;
    if (candidate.sender !== 'emma') return false;
    const lastEmma = [...prev].reverse().find(m => m.sender === 'emma');
    if (!lastEmma) return false;
    const sameText = (lastEmma.content || '').trim() === (candidate.content || '').trim();
    if (!sameText) return false;
    const lastTs = new Date(lastEmma.timestamp).getTime();
    const nextTs = new Date(candidate.timestamp).getTime();
    const deltaMs = Math.abs(nextTs - lastTs);
    return deltaMs <= 5000;
  }, []);

  // Phase 8: Tool Activity tracking hook for real-time tool feedback
  const { 
    activities: toolActivities, 
    isAnyActive: isToolActive,
    startActivity,
    completeActivity,
    failActivity 
  } = useToolActivity({ maxActivities: 10, autoCleanupMs: 15000 });

  // Context and WebSocket hooks
  const { context, updateContext, sessionId } = useEmmaContext();
  
  // Backend session sync (feature flag controlled - disabled by default)
  // When enabled, syncs conversation to PostgreSQL for cross-device persistence
  const { isBackendEnabled, forceSync } = useEmmaSessionSync({
    sessionId: sessionId || null,
    messages: messages.map(m => ({
      id: m.id,
      content: m.content,
      sender: m.sender as 'user' | 'emma',
      timestamp: m.timestamp
    })),
    contextState: context || {},
    onSessionRestored: (data) => {
      // Restore messages from backend when feature is enabled
      if (data.conversationHistory) {
        console.log('[EmmaChat] Session restored from backend:', data.conversationHistory.length, 'messages');
      }
    }
  });
  
  const { 
    isConnected, 
    connectionStatus, 
    sendMessage, 
    lastMessage, 
    typingIndicator,
    error,
    reconnect 
  } = useEmmaWebSocket({ 
    sessionId: sessionId || undefined,
    autoReconnect: true,
    maxReconnectAttempts: 10
  });

  // Handle errors (EMMA-HARDEN-002: Show ErrorRecoveryDialog)
  useEffect(() => {
    if (error) {
      setCurrentError({
        code: 'WEBSOCKET_ERROR',
        message: error,
        severity: 'error',
        recovery_actions: ['retry', 'refresh'],
        trace_id: `ws_${Date.now()}`
      });
    }
  }, [error]);

  // Auto-focus input when Emma finishes responding
  useEffect(() => {
    if (!isTyping) {
      // Small delay to ensure DOM is ready after any re-renders
      const focusTimeout = setTimeout(() => {
        inputRef.current?.focus();
      }, 50);
      return () => clearTimeout(focusTimeout);
    }
  }, [isTyping]);

  // Initialize context if provided
  useEffect(() => {
    if (initialContext) {
      updateContext(initialContext);
    }
  }, [initialContext, updateContext]);

  // Update context if propContext or campaignId change
  useEffect(() => {
    if (propContext) {
      updateContext(propContext);
    }
  }, [propContext, updateContext]);

  useEffect(() => {
    if (campaignId) {
      updateContext({ campaignId });
    }
  }, [campaignId, updateContext]);

  // Cross-Device Sync via BroadcastChannel API (Story UX-SALLY-001)
  useEffect(() => {
    // Only setup if BroadcastChannel is supported
    if (!('BroadcastChannel' in window)) return;

    try {
      const channel = new BroadcastChannel('emma_sync_channel');

      // Listen for messages from other tabs/devices
      channel.onmessage = (event) => {
        const { type, data } = event.data;
        
        if (type === 'message_sent' && data.message) {
          // Another tab sent a message - add it to this tab's conversation
          setMessages(prev => {
            if (isDuplicateEmmaMessage(prev, data.message)) return prev;
            return [...prev, data.message];
          });
          setLastActivityTime(new Date());
        } else if (type === 'message_received' && data.message) {
          // Receive Emma's response from another tab
          setMessages(prev => {
            if (isDuplicateEmmaMessage(prev, data.message)) return prev;
            return [...prev, data.message];
          });
          setIsTyping(false);
          setLastActivityTime(new Date());
        } else if (type === 'context_updated') {
          // Update context from another tab
          updateContext(data.context);
        }
      };

      // Cleanup on unmount
      return () => {
        channel.close();
      };
    } catch (error) {
      console.warn('BroadcastChannel not available for cross-device sync:', error);
    }
  }, [updateContext, isDuplicateEmmaMessage]);

  // Handle new messages from WebSocket
  useEffect(() => {
    if (lastMessage) {
      setMessages(prev => {
        const existingIndex = prev.findIndex(m => m.id === lastMessage.id);
        if (existingIndex !== -1) {
          const updated = [...prev];
          updated[existingIndex] = {
            ...updated[existingIndex],
            content: lastMessage.content || updated[existingIndex].content,
            type: lastMessage.type,
            metadata: {
              ...updated[existingIndex].metadata,
              ...lastMessage.metadata,
              thinkingContent: lastMessage.metadata?.thinkingContent || lastMessage.thinkingContent || updated[existingIndex].metadata?.thinkingContent,
            },
            isChunk: lastMessage.isChunk,
            isComplete: lastMessage.isComplete,
          };
          return updated;
        }

        if (isDuplicateEmmaMessage(prev, lastMessage)) return prev;
        
        return [...prev, {
          ...lastMessage,
          thinkingContent: lastMessage.metadata?.thinkingContent || lastMessage.thinkingContent,
        }];
      });

      // Clear typing state once we start receiving chunks or completed message
      if (lastMessage.isComplete || lastMessage.isChunk) {
        setIsTyping(false);
      }
      setTypingStatus('thinking');
      setSourcesChecked(0);
      setLastActivityTime(new Date());
      
      // Save session ID for cross-device recovery
      if (sessionId) {
        localStorage.setItem('emma_current_session_id', sessionId);
      }
      
      // Handle workflow status updates
      if (lastMessage.type === 'workflow_status') {
        setWorkflowStatus(lastMessage.workflowStatus || null);
      }

      // Handle typing status updates from Emma (EMMA-HARDEN-002)
      // Note: status_update may come via metadata even if not in type union
      const meta = lastMessage.metadata as any;
      if (meta?.status && ['thinking', 'searching', 'processing'].includes(meta.status)) {
        setTypingStatus(meta.status);
        setSourcesChecked(meta.sourcesChecked || 0);
      }

      // Phase 8: Handle tool activity events for real-time tool feedback
      if (meta?.toolActivity) {
        const { toolName, status, sourcesChecked: toolSources, activityId, error, errorType, details } = meta.toolActivity;
        if (status === 'start') {
          startActivity(toolName || 'knowledge_graph', details);
        } else if (status === 'complete') {
          completeActivity(activityId, toolSources);
        } else if (status === 'error') {
          failActivity(activityId, error);
          // Show user-friendly tool error message
          setToolError({
            toolName: toolName,
            errorType: errorType || 'generic',
            message: error,
            retryAction: () => {
              setToolError(null);
              // Re-send the last user message
              const lastUserMsg = messages.filter(m => m.sender === 'user').pop();
              if (lastUserMsg) handleSendMessage(lastUserMsg.content);
            },
            alternativeAction: {
              label: 'Try different approach',
              onClick: () => {
                setToolError(null);
                sendMessage('Please try a different approach to answer my question.');
              }
            }
          });
        }
      }
      
      // Handle workflow completion - proactive re-engagement
      if (lastMessage.type === 'workflow_complete') {
        setTimeout(() => {
          const reEngagementMessage: Message = {
            id: `emma_reengagement_${Date.now()}`,
            content: `Great! I've completed that workflow. Here are your next recommended actions...`,
            timestamp: new Date(),
            sender: 'emma',
            type: 'text',
            recommendations: lastMessage.nextActions || []
          };
          setMessages(prev => [...prev, reEngagementMessage]);
        }, 1000);
      }

      // Broadcast to other tabs/devices (Cross-Device Sync)
      if ('BroadcastChannel' in window) {
        try {
          const channel = new BroadcastChannel('emma_sync_channel');
          channel.postMessage({
            type: 'message_received',
            data: { message: lastMessage, sessionId }
          });
          channel.close();
        } catch (error) {
          console.warn('Failed to broadcast message to other tabs:', error);
        }
      }
    }
  }, [lastMessage, sessionId, isDuplicateEmmaMessage]);

  // Listen for stateless asset generation events
  useEffect(() => {
    const handleStatelessStart = () => {
      setIsTyping(true);
      setTypingStatus('processing');
    };

    const handleStatelessAsset = (e: Event) => {
      const customEvent = e as CustomEvent;
      const { asset, asset_type, deal_context } = customEvent.detail;
      
      const assetMessage: Message = {
        id: `stateless_asset_${Date.now()}`,
        content: `Here is the personalized ${asset_type || 'asset'} generated on-demand:`,
        timestamp: new Date(),
        sender: 'emma',
        type: 'text',
        metadata: {
          isStatelessAsset: true,
          statelessAsset: asset,
          assetType: asset_type,
          dealContext: deal_context
        }
      };
      
      setMessages(prev => [...prev, assetMessage]);
      setIsTyping(false);
    };

    const handleStatelessError = (e: Event) => {
      const customEvent = e as CustomEvent;
      const { message } = customEvent.detail;
      
      const errorMessage: Message = {
        id: `stateless_error_${Date.now()}`,
        content: message,
        timestamp: new Date(),
        sender: 'emma',
        type: 'error'
      };
      
      setMessages(prev => [...prev, errorMessage]);
      setIsTyping(false);
    };

    window.addEventListener('emma-stateless-start', handleStatelessStart);
    window.addEventListener('emma-stateless-asset', handleStatelessAsset);
    window.addEventListener('emma-stateless-error', handleStatelessError);
    
    return () => {
      window.removeEventListener('emma-stateless-start', handleStatelessStart);
      window.removeEventListener('emma-stateless-asset', handleStatelessAsset);
      window.removeEventListener('emma-stateless-error', handleStatelessError);
    };
  }, []);

  // Auto-scroll to bottom when new messages arrive
  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  // Handle message sending
  const handleSendMessage = useCallback((content: string) => {
    if (!content.trim() || !isConnected) return;

    // Create user message
    const userMessage: Message = {
      id: `user_${Date.now()}`,
      content: content.trim(),
      timestamp: new Date(),
      sender: 'user',
      type: 'text'
    };

    // Add to messages immediately for responsive UX
    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsTyping(true);

    // Send via WebSocket
    sendMessage(content.trim());
    onMessageSent?.(content.trim());

    // Broadcast to other tabs/devices (Cross-Device Sync)
    if ('BroadcastChannel' in window) {
      try {
        const channel = new BroadcastChannel('emma_sync_channel');
        channel.postMessage({
          type: 'message_sent',
          data: { message: userMessage, sessionId }
        });
        channel.close();
      } catch (error) {
        console.warn('Failed to broadcast message to other tabs:', error);
      }
    }

    // Focus back to input
    setTimeout(() => inputRef.current?.focus(), 100);
  }, [isConnected, sendMessage, onMessageSent, sessionId]);

  // Handle recommendation clicks
  const handleRecommendationClick = useCallback((recommendation: AgentRecommendation) => {
    onRecommendationSelected?.(recommendation);
    
    // Send follow-up message about recommendation selection
    const followUpMessage = `I'd like to proceed with the ${recommendation.agentName} recommendation: ${recommendation.action}`;
    handleSendMessage(followUpMessage);
  }, [onRecommendationSelected, handleSendMessage]);

  // Handle emergency help
  const handleEmergencyHelp = useCallback(() => {
    const helpMessage = "I need help with what's currently happening";
    handleSendMessage(helpMessage);
    
    // Immediately show Emma's response
    const emmaHelpResponse: Message = {
      id: `emma_help_${Date.now()}`,
      content: "I'm here to help! Let me explain what's happening and guide you through your options.",
      timestamp: new Date(),
      sender: 'emma',
      type: 'text'
    };
    setMessages(prev => [...prev, emmaHelpResponse]);
  }, [handleSendMessage]);

  // Story 2: Handle entity search (search for related entities in knowledge base)
  const handleSearch = useCallback(async (query: string, entityType?: string) => {
    if (!query.trim()) {
      setSearchError('Search query cannot be empty');
      return;
    }

    setIsSearching(true);
    setSearchError(null);
    setSearchResults([]);

    try {
      const response = await apiRequest('/api/v1/emma/search-entities', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionId || ''}`,
        },
        body: JSON.stringify({
          query: query.trim(),
          entity_type: entityType,
          limit: 5,
          organization_id: context?.organizationId,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `Search failed with status ${response.status}`);
      }

      const data = await response.json();
      
      // Extract results from response
      const results = data.results || [];
      setSearchResults(results);

      if (results.length === 0) {
        setSearchError('No related entities found. Try a different search query.');
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to search entities';
      setSearchError(errorMessage);
      console.error('Entity search error:', error);
    } finally {
      setIsSearching(false);
    }
  }, [sessionId, context?.organizationId]);

  // Handle search result selection
  const handleResultSelected = useCallback((result: any) => {
    setSelectedSearchResult(result);
    
    // Show recommendations for the selected entity (Story 3)
    setRecommendationsForEntity({
      entityId: result.id,
      entityType: result.type,
      entityName: result.name
    });
    
    // Optionally: send message about selection
    const followUpMessage = `I'm interested in learning more about ${result.name}`;
    handleSendMessage(followUpMessage);
  }, [handleSendMessage]);

  // Handle input key press
  const handleKeyPress = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage(inputValue);
    }
  }, [inputValue, handleSendMessage]);

  // Handle recommendation selection from carousel
  const handleRecommendationSelect = useCallback((entityId: string, entityName: string) => {
    // Send to parent component
    if (onRecommendationSelected) {
      onRecommendationSelected({
        type: 'recommendation_selected',
        entityId,
        entityName,
        sourceType: 'carousel'
      } as any);
    }

    // Add assistant message acknowledging selection
    const acknowledgeMessage: Message = {
      id: `emma_rec_ack_${Date.now()}`,
      content: `Great choice! I've selected **${entityName}**. Let me help you work with this entity.`,
      timestamp: new Date(),
      sender: 'emma',
      type: 'text'
    };
    setMessages(prev => [...prev, acknowledgeMessage]);
    
    // Clear recommendations panel
    setRecommendationsForEntity(null);
  }, [onRecommendationSelected]);

  // CCE-007: Handle classification suggestion acceptance
  const handleAcceptClassificationSuggestion = useCallback(async (suggestion: ClassificationSuggestion) => {
    try {
      await apiRequest(`/api/v1/classifications/${suggestion.document_id}`, {
        method: 'PATCH',
        body: JSON.stringify({
          facets: suggestion.suggested_changes,
          requires_human_review: true,
          review_reasons: ['emma_suggested_correction']
        }),
      });

      // Send confirmation to Emma
      const confirmMessage: Message = {
        id: `emma_class_confirm_${Date.now()}`,
        content: "I accepted your suggestion. Thanks for the help!",
        timestamp: new Date(),
        sender: 'user',
        type: 'text'
      };
      setMessages(prev => [...prev, confirmMessage]);

      // Emma's acknowledgment
      const ackMessage: Message = {
        id: `emma_class_ack_${Date.now()}`,
        content: "Perfect! I've updated the classification and flagged it for human review. The quality team will verify it shortly.",
        timestamp: new Date(),
        sender: 'emma',
        type: 'text'
      };
      setMessages(prev => [...prev, ackMessage]);

    } catch (error: any) {
      console.error('Failed to apply classification suggestion:', error);
      const errorMessage: Message = {
        id: `emma_class_error_${Date.now()}`,
        content: `I encountered an error applying the classification: ${error.message}. Please try again.`,
        timestamp: new Date(),
        sender: 'emma',
        type: 'error'
      };
      setMessages(prev => [...prev, errorMessage]);
    }
  }, []);

  // CCE-007: Handle classification suggestion rejection
  const handleRejectClassificationSuggestion = useCallback(async (suggestion: ClassificationSuggestion) => {
    // Send rejection to Emma
    const rejectMessage: Message = {
      id: `emma_class_reject_${Date.now()}`,
      content: "Actually, I don't think that's quite right. Can you suggest something else?",
      timestamp: new Date(),
      sender: 'user',
      type: 'text'
    };
    setMessages(prev => [...prev, rejectMessage]);

    // Emma's response
    const responseMessage: Message = {
      id: `emma_class_response_${Date.now()}`,
      content: "No problem! Can you tell me more about what you think the correct classification should be? That will help me suggest a better alternative.",
      timestamp: new Date(),
      sender: 'emma',
      type: 'text'
    };
    setMessages(prev => [...prev, responseMessage]);
  }, []);

  // Connection status indicator
  const renderConnectionStatus = () => {
    if (connectionStatus === 'connected') return null;

    return (
      <div className="flex items-center justify-center py-2 px-4 bg-yellow-50 dark:bg-yellow-900/20 border-b border-yellow-200 dark:border-yellow-700">
        <div className="flex items-center space-x-2">
          <div className={`w-2 h-2 rounded-full ${
            connectionStatus === 'connecting' ? 'bg-yellow-500 animate-pulse' : 'bg-red-500'
          }`} />
          <span className="text-sm text-yellow-800 dark:text-yellow-200">
            {connectionStatus === 'connecting' ? 'Connecting to Emma...' : 'Connection lost'}
          </span>
          {connectionStatus === 'disconnected' && (
            <button
              onClick={reconnect}
              className="text-sm bg-yellow-600 text-white px-2 py-1 rounded hover:bg-yellow-700 transition-colors"
            >
              Reconnect
            </button>
          )}
        </div>
      </div>
    );
  };

  // Error display
  const renderError = () => {
    if (!currentError) return null;

    return (
      <ErrorRecoveryDialog
        error={currentError}
        onClose={() => setCurrentError(null)}
        onRetry={() => {
          setCurrentError(null);
          reconnect();
        }}
      />
    );
  };

  return (
    <div 
      className={`flex flex-col h-full bg-white dark:bg-gray-900 ${className}`}
      role="region"
      aria-label="Conversation with Emma"
    >
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-primary-500 to-primary-600">
        <div className="flex items-center space-x-3">
          {/* Cosellus Logo (EMMA-HARDEN-002) */}
          <img
            src="/cosellus-logo.svg"
            alt="Cosellus"
            className="h-8 w-auto mr-2 rounded-md bg-white/95 px-2 py-1"
          />
          <div className="w-10 h-10 flex items-center justify-center bg-emma-500 rounded-full text-white font-bold shadow-lg">
            E
          </div>
          <div>
            <h2 className="text-lg font-semibold text-white">
              Emma
            </h2>
            <p className="text-sm text-white/90">
              {workflowStatus ? workflowStatus.description : 'Your AI Strategic Partner'}
            </p>
            <p className="text-xs text-white/60 mt-0.5">
              Emma is an AI assistant
            </p>
          </div>
        </div>
        
        <div className="flex items-center space-x-3">
          {/* Workflow Status Indicator */}
          {workflowStatus && (
            <WorkflowStatusIndicator 
              status={workflowStatus}
              className="mr-2"
            />
          )}
          
          {/* Emergency Help Button */}
          {isEmergencyHelpVisible && (
            <EmergencyHelpButton 
              onClick={handleEmergencyHelp}
              isVisible={workflowStatus?.type === 'agent_executing' || isTyping}
            />
          )}
          
          {/* Connection indicator */}
          <div className="flex items-center space-x-2">
            <div className={`w-3 h-3 rounded-full ${
              isConnected ? 'bg-emma-500' : 'bg-red-500'
            }`} />
            <span className="text-sm text-white/90">
              {isConnected ? 'Connected' : 'Offline'}
            </span>
          </div>
        </div>
      </div>

      {/* Connection status banner */}
      {renderConnectionStatus()}

      {/* Error display */}
      {renderError()}

      {/* Phase 8: Tool-specific error messages with recovery options */}
      {toolError && (
        <ToolErrorMessage
          error={{
            type: toolError.errorType || 'generic',
            toolName: toolError.toolName,
            timestamp: new Date()
          }}
          onRetry={toolError.retryAction}
          onDismiss={() => setToolError(null)}
          className="mx-4 mt-2"
        />
      )}

      {/* Messages area */}
      <div 
        className="flex-1 overflow-y-auto p-4 space-y-4"
        role="log"
        aria-label="Message history"
      >
        {messages.length === 0 && (
          <div className="text-center py-8">
            <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center bg-emma-500 rounded-full text-white text-2xl font-bold shadow-lg">
              E
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
              Hi! I'm Emma
            </h3>
            <p className="text-gray-600 dark:text-gray-400 max-w-md mx-auto mb-4">
              I'm your AI strategic partner. I can help coordinate agents, provide strategic guidance, 
              and simplify complex workflows. What would you like to work on today?
            </p>
            {/* Phase 8: Show capabilities explainer button */}
            <button
              onClick={() => setShowCapabilities(!showCapabilities)}
              className="text-sm text-primary-500 hover:text-primary-600 underline transition-colors"
            >
              {showCapabilities ? 'Hide capabilities' : 'See what I can help with →'}
            </button>
            {showCapabilities && (
              <EmmaCapabilitiesExplainer 
                className="mt-4 text-left"
              />
            )}
          </div>
        )}

        {messages.map((message) => {
          // Extract entities from Emma responses using utility function (not hook inside loop)
          const entities = message.sender === 'emma' && message.content ? extractEmmaEntities(message.content) : [];
          
          return (
            <div key={message.id} className="space-y-2">
              <ConversationBubble
                message={message}
                isOwn={message.sender === 'user'}
                onRecommendationClick={handleRecommendationClick}
              />
              
              {/* Emma UI action confirmation badge */}
              {message.metadata?.ui_action && (
                <div className="mx-4 pl-12 pr-4">
                  <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-green-50 border border-green-200 text-xs text-green-700">
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="font-medium">Updated:</span> {message.metadata.ui_action.summary || 'Campaign data updated'}
                  </div>
                </div>
              )}

              {/* CCE-007: Display classification suggestion if present */}
              {message.metadata?.classificationSuggestion && (
                <div className="mx-4 pl-12 pr-4">
                  <ClassificationSuggestionCard
                    suggestion={message.metadata.classificationSuggestion}
                    onAccept={handleAcceptClassificationSuggestion}
                    onReject={handleRejectClassificationSuggestion}
                  />
                </div>
              )}
              
              {/* Display extracted entities from Emma response */}
              {entities.length > 0 && (
                <div className="mx-4 pl-12 pr-4">
                  <EmmaEntityContext
                    entities={entities}
                    onEntitySelect={(entity) => {
                      setSelectedEntityId(entity.id);
                      // Notify parent if callback provided
                      if (onRecommendationSelected) {
                        onRecommendationSelected({
                          type: 'entity_selection',
                          entityType: entity.type,
                          entityId: entity.id,
                          entityName: entity.name
                        } as any);
                      }
                    }}
                    orgId={context?.organizationId}
                    className="text-sm"
                  />
                </div>
              )}
            </div>
          );
        })}

        {/* Story 2: Display search results in context */}
        {searchResults.length > 0 && (
          <div className="mx-4 my-4">
            <EmmaContextRenderer
              results={searchResults}
              isLoading={isSearching}
              error={searchError}
              onSelectResult={handleResultSelected}
            />
          </div>
        )}

        {/* Story 3: Display AI-powered recommendations carousel */}
        {recommendationsForEntity && (
          <div className="mx-4 my-4 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-lg p-4 border border-blue-200 dark:border-blue-800">
            <RecommendationCarousel
              entityId={recommendationsForEntity.entityId}
              entityType={recommendationsForEntity.entityType}
              limit={5}
              onSelect={handleRecommendationSelect}
              excludeIds={[]} // Can add currently selected entities to exclude
              className="mt-2"
            />
          </div>
        )}

        {/* Phase 8: Real-time tool activity indicator (enhanced typing indicator) */}
        {(isTyping || isToolActive || typingIndicator?.isVisible) && (
          <div className="space-y-2">
            {/* Show detailed tool activities when tools are running */}
            {toolActivities.length > 0 && (
              <ToolActivityIndicator
                activities={toolActivities}
                compact={false}
                showCompleted={true}
                className="mb-2"
              />
            )}
            {/* Fallback to descriptive typing indicator when no tool activities */}
            {(isTyping || typingIndicator.isVisible) && toolActivities.length === 0 && (
              <EmmaTypingIndicator
                status={typingStatus}
                message={typingIndicator.message}
                sourcesChecked={sourcesChecked}
                onCancel={() => {
                  setIsTyping(false);
                  setTypingStatus('thinking');
                  setSourcesChecked(0);
                }}
              />
            )}
          </div>
        )}

        {/* Scroll anchor */}
        <div ref={messagesEndRef} />
      </div>

      {/* Input area */}
      <div className="border-t border-gray-200 dark:border-gray-700 p-4">
        <div className="flex items-end space-x-3">
          <div className="flex-1 relative">
            <input
              ref={inputRef}
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder={isConnected ? "Ask Emma anything..." : "Connecting..."}
              disabled={!isConnected}
              className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-2xl bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none disabled:opacity-50 disabled:cursor-not-allowed"
              autoFocus
              aria-label="Message input"
            />
          </div>
          
          <button
            onClick={() => handleSendMessage(inputValue)}
            disabled={!inputValue.trim() || !isConnected}
            className="px-6 py-3 bg-primary-500 text-white rounded-2xl hover:bg-primary-600 focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            aria-label="Send message"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
            </svg>
          </button>
        </div>
        
        {/* Help text */}
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-2 text-center">
          Press Enter to send • Shift+Enter for new line
        </p>
      </div>
    </div>
  );
}
